import { Component, Input, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject, debounceTime, switchMap, of } from 'rxjs';
import { ServicoService } from '../../../modules/admin/services/servico.service';
import { ValidationBusService } from '../../services/validation-bus.service';

type TipoRegistro = 'recadastro' | 'cadastro';

@Component({
  selector: 'app-field-verify-toggle',
  standalone: false,
  templateUrl: './field-verify-toggle.component.html',
  styleUrls: ['./field-verify-toggle.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldVerifyToggleComponent implements OnInit {
  @Input({ required: true }) campoKey!: string;
  @Input({ required: true }) tipo: TipoRegistro = 'recadastro';
  @Input() fotoId?: string;
  @Input() fotoDocumentoId?: string;
  @Input() recadastroId?: string;
  @Input() cadastroId?: string;
  @Input() label = 'Validar';
  @Input() defaultChecked = true;
  @Input() disabled = false;
  @Input() observacaoEnabled = true;
  @Input() observacaoPlaceholder = 'Escreva uma observação (opcional)';

  checked = this.defaultChecked;
  observacao?: string;
  showObservacao = false;
  saving = false;
  error?: string;

  private changed$ = new Subject<void>();

  constructor(
    private servicos: ServicoService,
    private cdr: ChangeDetectorRef,
    private validationBus: ValidationBusService
  ) {}

  ngOnInit(): void {
    const id = this.getId();
    console.debug('[FieldVerifyToggle] init', { campo: this.campoKey, tipo: this.tipo, id });
    if (!id) return;
    // 1) Carrega estado atual (se existir); se não existir, inicializa como válido e envia para API
    // Se for uma validação atrelada a uma foto individual, use os endpoints de foto
    if (this.fotoId) {
      this.servicos.obterValidacaoFoto(this.fotoId).subscribe({
        next: (resp: any) => {
          // API pode retornar um objeto ou um array; normalizar para primeiro item
          const item = Array.isArray(resp) ? resp[0] : resp;
          if (item) {
            const validado = item.statusFlag === 'S' || item.validado === true || item.validado === 'S';
            this.checked = !!validado;
            this.observacao = item.observacao ?? undefined;
            this.showObservacao = this.observacaoEnabled && !this.checked;
            this.cdr.markForCheck();
            if (!this.checked && this.observacao && this.observacao.trim().length > 0) {
              try { this.validationBus.emitInvalidation({ campo: this.campoKey, observacao: this.observacao.trim(), registroId: this.fotoId, tipo: this.tipo }); } catch(e) { /* ignore */ }
            }
          } else {
            // Se não encontrou registro, inicia como válido e envia para API
            this.checked = true;
            this.cdr.markForCheck();
            this.changed$.next();
          }
        },
        error: () => { 
          // Se erro ao buscar, inicia como válido e envia para API
          this.checked = true;
          this.cdr.markForCheck();
          this.changed$.next();
        }
      });
    } else if (this.fotoDocumentoId) {
      this.servicos.obterValidacaoFotoDocumento(this.fotoDocumentoId).subscribe({
        next: (resp: any) => {
          const item = Array.isArray(resp) ? resp[0] : resp;
          if (item) {
            const validado = item.statusFlag === 'S' || item.validado === true || item.validado === 'S';
            this.checked = !!validado;
            this.observacao = item.observacao ?? undefined;
            this.showObservacao = this.observacaoEnabled && !this.checked;
            this.cdr.markForCheck();
            if (!this.checked && this.observacao && this.observacao.trim().length > 0) {
              try { this.validationBus.emitInvalidation({ campo: this.campoKey, observacao: this.observacao.trim(), registroId: this.fotoDocumentoId, tipo: this.tipo }); } catch(e) { /* ignore */ }
            }
          } else {
            // Se não encontrou registro, inicia como válido e envia para API
            this.checked = true;
            this.cdr.markForCheck();
            this.changed$.next();
          }
        },
        error: () => { 
          // Se erro ao buscar, inicia como válido e envia para API
          this.checked = true;
          this.cdr.markForCheck();
          this.changed$.next();
        }
      });
    } else {
      const fetch$ = this.tipo === 'recadastro'
        ? this.servicos.obterValidacoesRecadastro(id)
        : this.servicos.obterValidacoesCadastro(id);

      fetch$.subscribe({
        next: (itens: any[]) => {
          const atual = itens?.find(x => x?.campo === this.campoKey);
          if (atual) {
            this.checked = !!atual.validado;
            this.observacao = atual.observacao ?? undefined;
            // show observacao input when enabled and current state is invalid
            this.showObservacao = this.observacaoEnabled && !this.checked;
            this.cdr.markForCheck();
            // Emit existing invalidation so parent details page can pickup current obs
            if (!this.checked && this.observacao && this.observacao.trim().length > 0) {
              try { this.validationBus.emitInvalidation({ campo: this.campoKey, observacao: this.observacao.trim(), registroId: this.getId(), tipo: this.tipo }); } catch(e) { /* ignore */ }
            }
          } else {
            // Se não encontrou registro para este campo, inicia como válido e envia para API
            this.checked = true;
            this.cdr.markForCheck();
            this.changed$.next();
          }
        },
        error: () => { 
          // Se erro ao buscar, inicia como válido e envia para API
          this.checked = true;
          this.cdr.markForCheck();
          this.changed$.next();
        }
      });
    }

    // 2) Debounce do PATCH (otimista)
    this.changed$
      .pipe(
        debounceTime(250),
        switchMap(() => {
          const id2 = this.getId();
          if (!id2) return of(null);
          this.saving = true;
          this.error = undefined;
          this.cdr.markForCheck();

          // If a specific photo id was provided, call photo-specific endpoints
          if (this.fotoId) {
            const statusFlag = this.checked ? 'S' : 'N';
            return this.servicos.atualizarValidacaoFoto(this.fotoId, statusFlag, this.observacao);
          }
          if (this.fotoDocumentoId) {
            const statusFlag = this.checked ? 'S' : 'N';
            return this.servicos.atualizarValidacaoFotoDocumento(this.fotoDocumentoId, statusFlag, this.observacao);
          }

          const req$ = this.tipo === 'recadastro'
            ? this.servicos.atualizarValidacaoCampoRecadastro(id2, this.campoKey, this.checked, this.observacao)
            : this.servicos.atualizarValidacaoCampoCadastro(id2, this.campoKey, this.checked, this.observacao);

          return req$;
        })
      )
      .subscribe({
        next: () => { 
          this.saving = false; 
          this.cdr.markForCheck();
        },
        error: () => { 
          this.saving = false; 
          this.error = 'Falha ao salvar';
          this.cdr.markForCheck();
        }
      });
  }

  onToggle(ev: Event) {
    if (this.disabled) return;
    const val = (ev.target as HTMLInputElement).checked;
    this.checked = val;            // UI otimista
    if (this.observacaoEnabled && !val) {
      this.showObservacao = true;
      this.cdr.markForCheck();
      return;
    }
    if (val) {
      // Clear any previously entered observation (we're optimistic here)
      this.observacao = undefined;
      // Emit with no observation so parent can clean up auto-filled lines
      try {
        const registroId = this.fotoId || this.fotoDocumentoId || this.getId();
        this.validationBus.emitInvalidation({ campo: this.campoKey, observacao: undefined, registroId: registroId, tipo: this.tipo });
      } catch (e) { /* no-op: bus may not be available, keep behavior safe */ }
    }

    // otherwise save immediately (either observacao not required or marking valid)
    this.showObservacao = false;
    this.changed$.next();
  }

  onObsBlur() {
    if (this.disabled) return;
    this.changed$.next();          // salva ao sair do campo observação

    if (!this.checked && this.observacao && this.observacao.trim().length > 0) {
      const registroId = this.fotoId || this.fotoDocumentoId || this.getId();
      this.validationBus.emitInvalidation({ campo: this.campoKey, observacao: this.observacao.trim(), registroId: registroId, tipo: this.tipo });
    }
  }

  private getId(): string | undefined {
    return this.tipo === 'recadastro' ? this.recadastroId : this.cadastroId;
  }
}
